import {
  MAX_ENTRIES_PER_WRITE,
  type BoothLogEntryInput,
  type LogCategory,
  type LogLevel
} from '../../../../shared/boothLog'

/**
 * The renderer's side of the booth log file (src/shared/boothLog.ts).
 *
 * The renderer console never leaves the booth; the main-process log file does
 * (it is uploaded to R2). This sends chosen renderer lines into that file
 * through `api.logs.write`, so they reach the cloud with it.
 *
 * It costs nothing where it cannot work: a shell older than `logs.write`, a web,
 * Tauri or iPad booth (no `window.api` at all). There nothing is buffered and
 * nothing is sent. Where it can work, lines are batched (one IPC call every
 * couple of seconds, at once for an error) and throttled per kind, so a failure
 * repeating inside a 15 fps preview loop writes a handful of lines and a count,
 * not thousands. A logging call never throws into the code that made it.
 */

export interface BoothLogLine {
  level: LogLevel
  category: LogCategory
  /** A stable dotted key, e.g. `connect.fail`. */
  event: string
  message?: string
  data?: Record<string, unknown>
  /** Lines with one key share a throttle. Default: category + event + message. */
  throttleKey?: string
  /** How many lines one key may send per window. */
  maxPerWindow?: number
  /** The window, for kinds that can repeat for hours (an error screen nobody dismisses). */
  windowMs?: number
}

type Writer = (entries: BoothLogEntryInput[]) => Promise<unknown>

export const FLUSH_DELAY_MS = 2000
export const THROTTLE_WINDOW_MS = 30_000
export const DEFAULT_MAX_PER_WINDOW = 5
const MAX_BUFFERED = 500
const MAX_THROTTLE_KEYS = 300
const MAX_DATA_CHARS = 4000
const MAX_STACK_FRAMES = 5

/** `api.logs.write` when this shell has it, else null. */
export function defaultBoothLogWriter(): Writer | null {
  try {
    if (typeof window === 'undefined') return null
    const write = window.api?.logs?.write
    return typeof write === 'function' ? write : null
  } catch {
    return null
  }
}

/**
 * An error as fields, whatever was thrown. The v1 WASM build (every non-Canon
 * DSLR) throws a bare number, the address of a C++ exception, which used to
 * reach logs as just that number.
 */
export function describeError(err: unknown): Record<string, unknown> {
  try {
    if (err instanceof Error) {
      const out: Record<string, unknown> = { name: err.name, message: err.message.slice(0, 500) }
      for (const key of Object.keys(err)) {
        const value = (err as unknown as Record<string, unknown>)[key]
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
          out[key] = typeof value === 'string' ? value.slice(0, 300) : value
        }
      }
      const frames = (err.stack ?? '')
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line.startsWith('at '))
        .slice(0, MAX_STACK_FRAMES)
      if (frames.length > 0) out.stack = frames.join(' | ')
      return out
    }
    if (typeof err === 'number') {
      return {
        name: 'WasmException',
        message: `undecoded WASM exception (pointer ${err})`,
        pointer: err
      }
    }
    if (typeof err === 'string') return { message: err.slice(0, 500) }
    return { message: String(err).slice(0, 500) }
  } catch {
    return { message: 'unreadable error' }
  }
}

/**
 * Plain JSON: IPC refuses what it cannot clone (a USBDevice, a function), and
 * one bad field must not lose the line.
 */
export function toPlainData(data: Record<string, unknown>): Record<string, unknown> | undefined {
  try {
    const seen = new WeakSet<object>()
    const json = JSON.stringify(data, (_key, value: unknown) => {
      if (typeof value === 'bigint') return value.toString()
      if (typeof value === 'function' || typeof value === 'symbol') return undefined
      if (value instanceof Error) return describeError(value)
      if (typeof value === 'number' && !Number.isFinite(value)) return String(value)
      if (typeof value === 'object' && value !== null) {
        if (seen.has(value)) return '[circular]'
        seen.add(value)
      }
      return value
    })
    if (json === undefined) return undefined
    if (json.length > MAX_DATA_CHARS) return { truncated: true, preview: json.slice(0, 1000) }
    return JSON.parse(json) as Record<string, unknown>
  } catch {
    return { unserializable: true }
  }
}

interface ThrottleState {
  windowStart: number
  windowMs: number
  sent: number
  suppressed: number
  level: LogLevel
  category: LogCategory
  event: string
}

export class BoothLogClient {
  private buffer: BoothLogEntryInput[] = []
  private timer: ReturnType<typeof setTimeout> | null = null
  private timerDueAt = 0
  private throttles = new Map<string, ThrottleState>()
  private overflowed = 0

  constructor(private readonly getWriter: () => Writer | null = defaultBoothLogWriter) {}

  log(line: BoothLogLine): void {
    try {
      if (!this.getWriter()) return
      const now = Date.now()
      if (!this.admit(line, now)) return
      this.push({
        at: now,
        level: line.level,
        category: line.category,
        event: line.event,
        message: line.message ?? '',
        data: line.data ? toPlainData(line.data) : undefined
      })
      this.schedule(line.level === 'error' ? 0 : FLUSH_DELAY_MS)
    } catch {
      // Logging is never worth breaking the camera code that called it.
    }
  }

  /** Send everything buffered now. Also run on `pagehide`. */
  flush(): void {
    try {
      if (this.timer) clearTimeout(this.timer)
      this.timer = null
      const now = Date.now()
      this.reportThrottled(now, false)
      if (this.overflowed > 0) {
        this.buffer.push({
          at: now,
          level: 'warn',
          category: 'system',
          event: 'log.overflow',
          message: `${this.overflowed} renderer line(s) were dropped before they could be sent`
        })
        this.overflowed = 0
      }
      const write = this.getWriter()
      if (!write) {
        this.buffer = []
        return
      }
      while (this.buffer.length > 0) {
        const batch = this.buffer.splice(0, MAX_ENTRIES_PER_WRITE)
        try {
          void write(batch).catch(() => undefined)
        } catch {
          // A batch IPC could not clone is lost; the next one still goes.
        }
      }
      // A flood that stopped still owes its count once its window ends.
      const nextWindowEnd = this.nextThrottleWindowEnd()
      if (nextWindowEnd !== null) this.schedule(Math.max(0, nextWindowEnd - now))
    } catch {
      // see log()
    }
  }

  private admit(line: BoothLogLine, now: number): boolean {
    const key = line.throttleKey ?? `${line.category}|${line.event}|${line.message ?? ''}`
    const max = line.maxPerWindow ?? DEFAULT_MAX_PER_WINDOW
    let state = this.throttles.get(key)
    if (state && now - state.windowStart >= state.windowMs) {
      this.reportOne(key, state, now)
      state = undefined
    }
    if (!state) {
      if (this.throttles.size >= MAX_THROTTLE_KEYS) this.reportThrottled(now, true)
      state = {
        windowStart: now,
        windowMs: line.windowMs ?? THROTTLE_WINDOW_MS,
        sent: 0,
        suppressed: 0,
        level: line.level,
        category: line.category,
        event: line.event
      }
      this.throttles.set(key, state)
    }
    if (state.sent >= max) {
      state.suppressed++
      if (state.suppressed === 1) this.schedule(state.windowMs - (now - state.windowStart))
      return false
    }
    state.sent++
    return true
  }

  /** Close every window that has ended (or all of them), writing what each held back. */
  private reportThrottled(now: number, all: boolean): void {
    for (const [key, state] of this.throttles) {
      if (all || now - state.windowStart >= state.windowMs) this.reportOne(key, state, now)
    }
  }

  private reportOne(key: string, state: ThrottleState, now: number): void {
    this.throttles.delete(key)
    if (state.suppressed === 0) return
    this.push({
      at: now,
      level: state.level,
      category: state.category,
      event: 'log.throttled',
      message: `${state.suppressed} more "${state.event}" line(s) in ${Math.round(
        (now - state.windowStart) / 1000
      )}s were not sent`,
      data: { key: key.slice(0, 200), suppressed: state.suppressed }
    })
  }

  private nextThrottleWindowEnd(): number | null {
    let next: number | null = null
    for (const state of this.throttles.values()) {
      if (state.suppressed === 0) continue
      const end = state.windowStart + state.windowMs
      if (next === null || end < next) next = end
    }
    return next
  }

  private push(entry: BoothLogEntryInput): void {
    if (this.buffer.length >= MAX_BUFFERED) {
      this.buffer.shift()
      this.overflowed++
    }
    this.buffer.push(entry)
  }

  /** One timer, at the earliest time anything asked for. */
  private schedule(delayMs: number): void {
    const dueAt = Date.now() + delayMs
    if (this.timer && this.timerDueAt <= dueAt) return
    if (this.timer) clearTimeout(this.timer)
    this.timerDueAt = dueAt
    this.timer = setTimeout(() => this.flush(), delayMs)
  }
}

/** The renderer's one client. */
export const boothLog = new BoothLogClient()

try {
  if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
    // Best effort: a booth quit destroys the window without pagehide, but a reload fires it.
    window.addEventListener('pagehide', () => boothLog.flush())
  }
} catch {
  // see BoothLogClient.log
}
